import PatientSignatureImage from './PatientSignatureImage';

const MigsConsent = ({
    pageRefs,
    currentPage,
    logo,
    pageTitles,
    SurgeryFormData,
    surgeonOptionsMap,
    docSignature,
    setSignatures,
    setSurgeryFormData,
    handleInputChange,
    invalidFields,
    isNext,
    isEditing,
    signatures,
    canvasRefs,
    updateSignatureData,
    clearSignature,
    toggleEditMode,
    getCentralAmericaDate,
    getCentralAmericaTime,
    Logo,
    Head,
    Paragraph,
    RadioGroup,
    TextInput,
    SignatureField,
    SegLogoBanner,
    Initials,
    surgeryUpload,
    FormInput,
    MultiSelectorWithoutEyes,
    handleCheckboxChange,
    formattedDate,
    SegFooter

}) => {
    const practice = localStorage.getItem("digital-form-dept-practice");

    return (
        <div ref={pageRefs.current[currentPage]} className="max-w-5xl w-full bg-white">
            <SegLogoBanner src={logo} />

            <div className="px-1 mx-8 py-2">
                <Head title="Informed Consent for Minimally Invasive Glaucoma Surgery (MIGS)" />

                <Paragraph>
                    You have glaucoma. Glaucoma is a disease defined by damage to your optic nerve. The optic nerve
                    connects your eye to your brain. When fluid cannot drain properly, pressure builds in your eye and
                    damages the nerve. Glaucoma slowly worsens over time and cannot be reversed. If it is not treated, it
                    can cause painless vision loss and may eventually lead to blindness.
                </Paragraph>
                <Paragraph>
                    <span className="font-bold">Alternatives (choices and options)</span>. The best choices for glaucoma treatment are those that lower your
                    eye pressure with the fewest risks to your eyesight and overall health.
                </Paragraph>
                <ul className="p-3 list-disc font-medium list-inside text-black">
                    <li>Eye drop medications or laser therapy are typically used first. Often, multiple medications are
                        needed to reach the desired pressure level.</li>
                    <li>
                        If medications and laser treatment do not work well enough, or if using eye drops is difficult
                        because of cost, side effects, or other challenges, glaucoma surgery may be recommended.
                    </li>
                    <li>You may also choose to have no treatment. Without treatment, your glaucoma can get worse,
                        leading to additional vision loss and possible blindness.</li>
                </ul>
                <Paragraph>
                    There are newer surgeries called Minimally Invasive Glaucoma Surgery (MIGS). MIGS helps control your
                    eye pressure with fewer complications than traditional glaucoma surgery. These procedures modify your
                    eye's natural drainage system to lower pressure and may reduce your need for medications. MIGS is
                    performed through an incision less than 3 mm long (smaller than 1/8 inch). Your ophthalmologist will
                    discuss which MIGS procedure is best for you.
                </Paragraph>
                <span className="underline font-bold">iStent Trabecular Microbypass or Hydrus Microstent</span>
                <Paragraph>
                    Your ophthalmologist implants one of these devices in an area of your eye called Schlemm’s Canal to
                    allow fluid to leave your eye more easily. The stents are made of inactive titanium and are safe for MRI.
                </Paragraph>
                <span className="underline font-bold">Goniotomy (MRI Conditional)</span>
                <Paragraph>
                    Your ophthalmologist makes a small incision in a part of your eye’s drainage system called the trabecular
                    meshwork to help fluid flow more freely. No device is implanted.
                </Paragraph>
                <span className="underline font-bold">Canaloplasty</span>
                <Paragraph>
                    Your ophthalmologist injects a non-toxic jelly to inflate and stretch parts of your eye’s drainage system
                    (the trabecular meshwork, Schlemm’s Canal, and collector channels) to help fluid leave your eye. No
                    device is implanted. It works a bit like a gentle “roto-rooter,” clearing and restoring your eye’s natural
                    drainage pathway.
                </Paragraph>
                <span className="underline font-bold">Goniosynechialysis</span>
                <Paragraph>
                    Your ophthalmologist removes or reduces scar tissue from your eye’s drainage system to help fluid flow
                    out more easily. No device is implanted.
                </Paragraph>
                <Paragraph>
                    <span className="underline font-bold">Benefits (how the surgery can help)</span>. The goal of MIGS is to lower your eye pressure and prevent
                    additional vision loss. MIGS will not restore vision that has already been lost from glaucoma.
                    Risks (problems the surgery may cause).
                </Paragraph>
                <Paragraph>
                    <span className="underline font-bold">As with any surgical procedure, there are risks with MIGS</span>. The
                    surgery may not lower your eye pressure or control your glaucoma, even when it is properly performed.
                    Your ophthalmologist cannot list every possible risk, but the most common or serious include:
                </Paragraph>
                <ul className="p-3 list-disc font-medium list-inside text-black">
                    <li>Failure to control your eye pressure, which may require continued use of eye drops, laser treatment, or another surgery</li>
                    <li>Abnormal collection of fluid inside your eye, which may require another surgery</li>
                    <li>Worsening or loss of vision</li>
                    <li>Eye pressure that is too low</li>
                    <li>Damage to your eyeball</li>
                    <li>Infection</li>
                    <li>Bleeding inside your eye</li>
                    <li>Inflammation</li>
                    <li>A mechanical problem with an implant that may require removal or repositioning</li>
                    <li>Development of a cataract or clouding of the lens (except if you have already had cataract surgery or if you are having cataract surgery at the same time as this glaucoma surgery)</li>
                    <li>Pain, irritation, or discomfort in your eye or surrounding tissues that may persist</li>
                    <li>Drooping of your eyelid</li>
                    <li>Double vision</li>
                    <li>Problems during surgery that require immediate treatment. Your surgeon may need to perform additional procedures or change your surgery plan to address a new problem.</li>
                    <li>Other potential risks. There is no guarantee that the surgery will improve your vision. The surgery or anesthesia may worsen your vision, cause blindness, or even result in the loss of an eye. These problems can occur weeks, months, or even years after surgery.</li>
                </ul>

                <span className="font-bold">By signing below, you consent (agree) that:</span>

                <ul className="p-3 list-disc font-medium list-inside text-black">
                    <li>You read this informed consent form or had it read to you.</li>
                    <li>
                        You were told you have glaucoma.
                    </li>
                    <li>Your questions were answered about MIGS and the surgery recommended for you.</li>
                    <li>You consent to have the ophthalmologist perform MIGS on your   <span className="font-bold">{SurgeryFormData.eye === "ou"
                        ? "both"
                        : SurgeryFormData.eye === "od"
                            ? "right"
                            : SurgeryFormData.eye === "os"
                                ? "left"
                                : "none"}</span> (“right,” “left”) eye.</li>
                </ul>

                <div className="my-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div className="p-3 rounded-2xl border bg-gray-50">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">MIGS Procedure - OS</h3>
                        <MultiSelectorWithoutEyes
                            packages={SurgeryFormData.migs_procedure.os}
                            onChange={(pkg, value) =>
                                handleCheckboxChange("migs_procedure.os", pkg, value)
                            }
                            label=""
                        />
                    </div>

                    <div className="p-3 rounded-2xl border bg-gray-50">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">MIGS Procedure - OD</h3>
                        <MultiSelectorWithoutEyes
                            packages={SurgeryFormData.migs_procedure.od}
                            onChange={(pkg, value) =>
                                handleCheckboxChange("migs_procedure.od", pkg, value)
                            }
                            label=""
                        />
                    </div>
                </div>
                <div className="my-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <PatientSignatureImage
                        signature={SurgeryFormData['patient_signature']}
                        label="Patient Signature"
                        signedDate={SurgeryFormData.patientSignedDate}
                    />

                    <PatientSignatureImage
                        signature={SurgeryFormData['doctor_signature']}
                        label="Physician/Surgeon"
                        signedDate={formattedDate}
                    />

                </div>
            </div>
            <div className="mb-2">
                <SegFooter />
            </div>
        </div>
    );
};

export default MigsConsent;